import React from 'react';
import { inject, observer } from 'mobx-react';
import H5PostList from './h5';
import PCPostList from './pc';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';

interface PostListProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction
    threadId: any   // 帖子id
    threadType: any // 帖子类型, 0 主题 1 活动 2 反馈 3 专题
    commentType?: number  // 发布评论时的type参数 0=帖子评论 2=活动评论，默认0
    handleSetState?: any  // 父组件 setState 回调，用于更新选中项 id 等
    onPostLike?: (data: any) => void  // 评论点赞事件回调
    onDeleteComment?: (itemId: any) => Promise<void>  // 删除评论回调
    onSinkComment?: (itemId: any, currentIsSink?: boolean) => Promise<void>    // 沉贴评论回调，传入当前是否已沉贴
    onStickyComment?: (itemId: any, currentIsSticky?: boolean) => Promise<void>  // 置顶评论回调，传入当前是否已置顶
}

interface PostListState {
    postList: any
    currentPage: any
    filterList: any
    filterCurrentType: any
    filterCurrentIndex: any
}

@inject('app', 'site', 'user', 'thread')
@observer
class PostList extends React.Component<PostListProps, PostListState> {
    namespace='postList';
    private hasAppliedHighlight = false;

    isAdminUser = () => {
        return !!this.props.user?.userInfo?.group?.isAdmin;
    }

    clearPostIdFromUrl = () => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        if (!url.searchParams.has('postId')) return;
        url.searchParams.delete('postId');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    isDeletedOrInvisiblePost = (post: any) => {
        if (!post) return true;
        if (post?.isDeleted === true || Number(post?.isDeleted) === 1) return true;
        if (post?.isDisplay === false || Number(post?.isDisplay) === 0) return true;
        return false;
    }
    state: PostListState = {
        currentPage: 1, // 评论列表当前页
        postList: [],
        filterList: [
            {name: '默认', id: 0, type: 'id'},
            {name: '最新', id: 1, type: '-id'},
            {name: '最热', id: 2, type: '-likeCount'},
        ],
        filterCurrentType: 'id',    // 筛选的类型
        filterCurrentIndex: 0,  // 筛选的索引
    };
    // 删除评论
    handleDeletPost = () => {
        console.log('删除');
    }

    // 举报评论
    handleReportPost = () => {
        console.log('举报');
    }

    async componentDidMount() {
        await this.fetchPostList(1);
    }

    // 获取高亮评论（从 URL 参数 postId 获取）
    fetchHighlightPost = async () => {
        const { thread, threadId, user } = this.props;
        if (!thread) return;

        const urlParams = new URLSearchParams(window.location.search);
        const postId = urlParams.get('postId');
        const list = thread.listStore?.lists?.['postList'];
        const firstPageData = list?.data?.['1'] || [];

        console.error('[threadDetail][highlight] start', {
            href: window.location.href,
            threadId,
            postId,
            hasThreadStore: !!thread,
            firstPageCountBeforeFetch: Array.isArray(firstPageData) ? firstPageData.length : 'not-array',
            firstPageIdsBeforeFetch: Array.isArray(firstPageData) ? firstPageData.slice(0, 10).map((item: any) => item?.id || item?._id) : [],
        });

        if (!postId) {
            console.error('[threadDetail][highlight] skip: no postId in url');
            return;
        }

        // 非管理员且非帖子作者不走查看指定评论的权限，因为评论可能已删除
        const isAdmin = user?.userInfo?.group?.isAdmin;
        const isAuthor = user?.userInfo?.code && thread.threadData?.user?.code && user.userInfo.code === thread.threadData.user.code;
        if (!isAdmin && !isAuthor) {
            console.error('[threadDetail][highlight] skip: user is not admin or author');
            return;
        }

        try {
            const res = await thread.getPostDetail(postId);
            const singlePost = res?.data?.data || res?.data || res?.Data?.data || res?.Data;
            const isAdminUser = this.isAdminUser();
            const isValidResponse = res?.code === 0 || res?.Code === 0;
            const isDeletedOrInvisiblePost = this.isDeletedOrInvisiblePost(singlePost);

            console.error('[threadDetail][highlight] detail response', {
                postId,
                code: res?.code ?? res?.Code,
                topLevelKeys: res ? Object.keys(res) : [],
                dataKeys: res?.data ? Object.keys(res.data) : res?.Data ? Object.keys(res.Data) : [],
                singlePostId: singlePost?.id || singlePost?._id,
                singlePostThreadId: singlePost?.threadId,
                singlePostType: singlePost?.type,
                singlePostRaw: singlePost,
                isAdminUser,
                isDeletedOrInvisiblePost,
            });

            if (!isAdminUser && (!isValidResponse || isDeletedOrInvisiblePost)) {
                this.clearPostIdFromUrl();
                console.error('[threadDetail][highlight] cleared postId for non-admin deleted/invisible post', {
                    postId,
                    code: res?.code ?? res?.Code,
                });
                return;
            }

            if (isValidResponse && singlePost) {
                thread.prependPostToList(singlePost);
                this.deduplicateFirstPage();

                const nextList = thread.listStore?.lists?.['postList'];
                const nextFirstPageData = nextList?.data?.['1'] || [];
                console.error('[threadDetail][highlight] after prepend', {
                    postId,
                    insertedId: singlePost?.id || singlePost?._id,
                    firstItemIdAfterPrepend: Array.isArray(nextFirstPageData) && nextFirstPageData[0] ? (nextFirstPageData[0]?.id || nextFirstPageData[0]?._id) : null,
                    firstPageCountAfterPrepend: Array.isArray(nextFirstPageData) ? nextFirstPageData.length : 'not-array',
                    firstPageIdsAfterPrepend: Array.isArray(nextFirstPageData) ? nextFirstPageData.slice(0, 10).map((item: any) => item?.id || item?._id) : [],
                });
            } else {
                console.error('[threadDetail][highlight] skip prepend: invalid response', {
                    postId,
                    code: res?.code ?? res?.Code,
                    hasSinglePost: !!singlePost,
                });
            }
        } catch (error: any) {
            console.error('[threadDetail][highlight] fetch detail failed', {
                postId,
                message: error?.message,
                error,
            });
        }
    }

    // 对第一页评论列表去重
    deduplicateFirstPage = () => {
        const { thread } = this.props;
        if (!thread || !thread.listStore) return;
        
        const list = thread.listStore.lists['postList'];
        if (!list || !list.data) return;
        
        // 获取第一页数据
        const pageKeys = Object.keys(list.data);
        const firstPageKey = pageKeys.length ? String(Math.min(...pageKeys.map(page => Number(page)))) : '1';
        const firstPageData = list.data[firstPageKey];
        
        if (!Array.isArray(firstPageData)) return;
        
        console.log('🔄 第一页去重前数据条数:', firstPageData.length);
        
        // 使用 Map 去重，保留第一次出现的项，删除后续重复项
        const idSet = new Set();
        const deduplicatedData = firstPageData.filter(item => {
            const itemId = item?.id || item?._id;
            if (idSet.has(itemId)) {
                console.log('🗑️ 删除重复评论ID:', itemId);
                return false; // 过滤掉重复项
            }
            idSet.add(itemId);
            return true;
        });
        
        console.log('🔄 第一页去重后数据条数:', deduplicatedData.length);
        
        // 更新列表数据
        if (deduplicatedData.length !== firstPageData.length) {
            list.data[firstPageKey] = deduplicatedData;
            // 触发 MobX 响应式更新
            thread.listStore.lists = { ...thread.listStore.lists };
        }
    }

    componentDidUpdate(prevProps: Readonly<PostListProps>) {
        if (prevProps.threadId !== this.props.threadId) {
            this.hasAppliedHighlight = false;
            this.setState({ currentPage: 1 }, () => this.fetchPostList(1));
            return;
        }
    }

    // 筛选点击切换
    filterClick= (item, index) => {
        console.log(item, '筛选');
        this.setState({
            filterCurrentType: item.type,
            filterCurrentIndex: index
        }, () => {
            this.hasAppliedHighlight = false;
            this.fetchPostList(1);
        })
    }

    fetchPostList = async (page: number) => {
        const { thread, threadType, threadId } = this.props;
        // console.log(threadType, '帖子类型');
        const { filterCurrentType } = this.state;
        if (!thread || !threadId) return;
        const params = {
            'filter[id]': threadId,
            'filter[threadId]': threadId,
            'filter[type]': typeof threadType === 'undefined' ? 0 : threadType,
            'page[number]': page,
            'page[size]': 20,    // 每页20条
            sort: filterCurrentType,   // 最热 -likeCount，顺序 id，倒序 -id（前面是负号）
        };
        await thread.getPostList(params);
        this.setState({ currentPage: page });

        // 高亮评论：必须在列表接口完成并且渲染队列跑过之后再执行，
        // 否则 prepend 会被 setList 的整页覆盖冲掉。
        if (page === 1 && !this.hasAppliedHighlight) {
            const urlPostId = new URLSearchParams(window.location.search).get('postId');
            if (urlPostId) {
                this.hasAppliedHighlight = true;
                setTimeout(() => this.fetchHighlightPost(), 0);
            }
        }
    }
    // 加载分页
    handleLoadMore = () => {
        const nextPage = (this.state.currentPage || 1) + 1;
        this.fetchPostList(nextPage);
    }

    // 评论点赞
    handlePostLike = async (data: any) => {
        const { thread } = this.props;
        const id = thread?.getId ? thread.getId(data) : data?.id;
        if (thread && id) {
            const params = {
                id,
                type: 'post',
                isLiked: !data.isLiked,
                likeCount: data.likeCount
            };
            await thread.createUserslikes(params).then(async res => {
                if (res.code === 0) {
                    // 点赞成功后刷新评论列表
                    this.fetchPostList(this.state.currentPage || 1);
                }
            });
        }
    }

    render() {
        const { site, app, thread, handleSetState, onPostLike, ...props } = this.props;
        let { filterList, filterCurrentIndex } = this.state;
        const platform = app?.platform || 'h5';
        if (!thread || !thread.listStore) return null;
        const list = thread.listStore.lists['postList'];
        if (!list) return null;
        const postList = thread.listStore.getList({ namespace: 'postList' });
        const hasMore = typeof list.hasMore === 'boolean' ? list.hasMore : list.attribs?.hasMore;
        const loading = list.loading;
        const commonProps = {
            ...props,
            handleSetState,
            filterList,
            postList,
            hasMore,
            loading,
            onLoadMore: this.handleLoadMore,
            onPostLike: onPostLike || this.handlePostLike,
        };
        if (platform === 'pc') {
            return <PCPostList filterClick={(item, index) => this.filterClick(item, index)} filterCurrentIndex={filterCurrentIndex} {...commonProps}/>;
        } else {
            return <H5PostList filterClick={(item, index) => this.filterClick(item, index)} filterCurrentIndex={filterCurrentIndex} {...commonProps} />;
        }
    }
}

export default PostList;